import React, { useState } from 'react';
import { Table, Badge } from 'react-bootstrap';
import '../SearchMain.css';

export default function ArticlesOffcanvas(props) {
  const { selectedBook } = props;
  const article = selectedBook || {};
  const [copied, setCopied] = useState(false);

  const callNumber = article.callNo || article.journalCallNo || article.bookClassificationNo || article.classificationNo || '';
  const journalName = article.journalTitle || article.bookTitle || article.sourceJournal || '';

  const handleCopyCallNo = () => {
    const textToCopy = callNumber || article.journalID?.toString() || '';
    if (textToCopy && navigator.clipboard) {
      navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="p-2">
      {/* Article Icon Header & Title */}
      <div className="text-center mb-3">
        <div
          className="d-inline-flex justify-content-center align-items-center mb-2"
          style={{
            width: '72px',
            height: '72px',
            borderRadius: '18px',
            background: 'linear-gradient(135deg, #09475f 0%, #0bb6d7 100%)',
            color: '#ffffff',
            fontSize: '32px',
            boxShadow: '0 6px 18px rgba(9, 71, 95, 0.3)',
          }}
        >
          <i className="bi bi-file-earmark-text"></i>
        </div>
        <div className="fw-bold fs-6 text-dark">{article.articleTitle || 'Untitled Article'}</div>
        {article.articleSubTitle && article.articleSubTitle !== 'NA' && (
          <div className="text-muted small mb-1 fst-italic">{article.articleSubTitle}</div>
        )}
        <div className="text-secondary small">
          <i className="bi bi-person me-1"></i>{article.articleAuthor || 'Unknown Author'}
        </div>
      </div>

      {/* Volume / Shelf Reference Card */}
      <div className="vj-shelf-location-card">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <span className="small fw-bold text-muted text-uppercase">
            <i className="bi bi-journal-check text-primary me-1"></i> Periodical Reference
          </span>
          <Badge bg="primary">
            {article.volissuesNo ? `Vol. ${article.volissuesNo}` : 'Scholarly Article'}
          </Badge>
        </div>

        {journalName && (
          <div className="small fw-bold text-dark my-1">
            <i className="bi bi-journal-album text-primary me-1"></i> Journal: {journalName}
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center my-1">
          <div className="vj-shelf-callno">
            Call No: {callNumber || 'Periodicals Section'}
          </div>
          {callNumber && (
            <button
              type="button"
              className="btn btn-sm btn-outline-primary py-0 px-2 d-flex align-items-center gap-1"
              onClick={handleCopyCallNo}
              title="Copy Call Number"
              style={{ fontSize: '0.78rem', borderRadius: '6px' }}
            >
              <i className={`bi ${copied ? 'bi-check-lg text-success' : 'bi-clipboard'}`}></i>
              {copied ? 'Copied' : 'Copy'}
            </button>
          )}
        </div>

        <div className="d-flex justify-content-between align-items-center small text-muted border-top pt-1 mt-1">
          <span>Journal / Vol ID: <strong>#{article.journalID || article.articleID}</strong></span>
          <span>Pages: <strong>pp. {article.startPageNo || '1'} - {article.endPageNo || 'N/A'}</strong></span>
        </div>
      </div>

      {/* Article Bibliographic Details */}
      <div className="border rounded bg-white overflow-hidden shadow-sm">
        <Table hover size="sm" className="mb-0 align-middle">
          <tbody>
            <tr>
              <th className="bg-light text-muted small ps-3" style={{ width: '38%' }}>Article Title</th>
              <td className="fw-semibold">{article.articleTitle}</td>
            </tr>
            {article.articleSubTitle && article.articleSubTitle !== 'NA' && (
              <tr>
                <th className="bg-light text-muted small ps-3">Subtitle</th>
                <td className="fst-italic">{article.articleSubTitle}</td>
              </tr>
            )}
            <tr>
              <th className="bg-light text-muted small ps-3">Author(s)</th>
              <td>{article.articleAuthor || 'Unknown'}</td>
            </tr>
            {journalName && (
              <tr>
                <th className="bg-light text-muted small ps-3">Host Journal</th>
                <td className="fw-bold text-primary">{journalName}</td>
              </tr>
            )}
            {callNumber && (
              <tr>
                <th className="bg-light text-muted small ps-3">Call Number</th>
                <td className="font-monospace text-primary fw-semibold">{callNumber}</td>
              </tr>
            )}
            {article.volissuesNo && (
              <tr>
                <th className="bg-light text-muted small ps-3">Volume / Issue</th>
                <td>{article.volissuesNo}</td>
              </tr>
            )}
            {article.internalVolIssueNo && (
              <tr>
                <th className="bg-light text-muted small ps-3">Internal Vol/Issue</th>
                <td>{article.internalVolIssueNo}</td>
              </tr>
            )}
            <tr>
              <th className="bg-light text-muted small ps-3">Pages</th>
              <td>
                pp. {article.startPageNo || '1'} - {article.endPageNo || 'N/A'}
              </td>
            </tr>
            {article.articleKeywords && (
              <tr>
                <th className="bg-light text-muted small ps-3">Keywords</th>
                <td>{article.articleKeywords}</td>
              </tr>
            )}
            {article.journalID && (
              <tr>
                <th className="bg-light text-muted small ps-3">Host Journal ID</th>
                <td>#{article.journalID}</td>
              </tr>
            )}
            {article.articleDescription && (
              <tr>
                <th className="bg-light text-muted small ps-3">Abstract / Summary</th>
                <td className="small text-muted" style={{ whiteSpace: 'pre-wrap' }}>
                  {article.articleDescription}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Student Reference Tip */}
      <div className="mt-3 p-2 bg-light rounded small text-muted text-center border">
        <i className="bi bi-info-circle text-primary me-1"></i>
        To read this article, request bound volume <strong>#{article.journalID || article.articleID}</strong>
        {callNumber ? ` (Call No: ${callNumber})` : ''} at the Serials & Reference Desk.
      </div>
    </div>
  );
}
